import ReactBootstrapDropdown from 'react-bootstrap/Dropdown';

import React from 'react';
import Immutable, { isImmutable } from 'immutable';
import { type Collection } from 'immutable';
import classNames from 'classnames';
import SelectableContext from '@restart/ui/SelectableContext';

type Booleanish = boolean | 'true' | 'false';

import styles from 'css/src/components/common/ui/Dropdown.module.scss';

import type { DebouncedFunc } from 'lodash';
import debounce from 'lodash/debounce';

import Button, { SPINNER_ICON } from 'js/src/components/common/ui/Button';
import Spinner from 'js/src/components/common/ui/Spinner';
import type { Placement } from 'js/src/components/common/ui/Tooltip';
import Tooltip from 'js/src/components/common/ui/Tooltip';
import type { DROPDOWN_ITEM_TEXT_STYLES, ImmutableMap } from 'js/src/libs/consts';
import BlueprintsIcon from 'js/src/libs/custom_icons/BlueprintsIcon';
import SmartEditIcon from 'js/src/libs/custom_icons/SmartEditIcon';
import SmartMediaIcon from 'js/src/libs/custom_icons/SmartMediaIcon';
import SmartSearchIcon from 'js/src/libs/custom_icons/SmartSearchIcon';
import uuid from 'js/src/libs/uuid';

const CUSTOM_ICONS_MAP: {
    [key: string]: React.ReactNode;
} = {
    blueprints: <BlueprintsIcon />,
    smart_search: <SmartSearchIcon fillColor="currentColor" />,
    smart_edit: <SmartEditIcon />,
    smart_media: <SmartMediaIcon />,
};

// delay time for sub menus to open/close, allows for accidental hovers
// https://lumen5.slack.com/archives/C0730L096JK/p1717783045215269
const MOUSEENTER_DELAY_MS = 250;
type AnyCallback = (...args: any[]) => any;
type DebouncedCallback = DebouncedFunc<AnyCallback>;
/**
 * To allow for accidental hovers to be ignored, we need to implement a debounce
 * on the opening/closing of submenus. This class maintains the callbacks
 * so that they are cancellable depending on situation.
 */
class DebouncedSetMenuPathManager {
    debounceTime: number;
    callbacks: Map<string, DebouncedCallback>;

    constructor(debounceTime: number) {
        this.debounceTime = debounceTime;
        this.callbacks = new Map();
    }

    _createKey(args: any[]) {
        return `${JSON.stringify(args.join('')).replace(/"/g, '')}*${uuid()}`;
    }

    _getPath(key: string) {
        return key.split('*')[0];
    }

    addCallback(callback: AnyCallback, debounceTime: number) {
        const debouncedCallback = debounce((...args) => {
            // eslint-disable-next-line
            callback(...args);
        }, debounceTime);

        return (...args: any[]) => {
            const argsKey = this._createKey(args);

            // rootClose called, prioritize this over other queued callbacks
            if (this._getPath(argsKey) === '') {
                this.callbacks.forEach((callback) => callback.cancel());
                // eslint-disable-next-line
                callback(...args);
                return;
            }

            // Set the arguments as the key to the debounced callback in the map
            this.callbacks.set(argsKey, debouncedCallback);
            this.cancelAllCallbacks(argsKey);
            debouncedCallback(...args);
        };
    }

    cancelAllCallbacks(nextCallbackArgsKey: string) {
        this.callbacks.forEach((callback, previousCallbackKey) => {
            if (nextCallbackArgsKey === previousCallbackKey) {
                return;
            }
            const path = this._getPath(previousCallbackKey).split('-');
            const sharesPath = path.some((p) => nextCallbackArgsKey.indexOf(p) >= 0);
            sharesPath && callback.cancel();
            this.callbacks.delete(previousCallbackKey);
        });
    }
}

const debounceManager = new DebouncedSetMenuPathManager(MOUSEENTER_DELAY_MS);

type DefaultToggleProps = {
    text?: string;
    onClick: VoidFunction;
    'aria-expanded'?: Booleanish;
    ref?: React.Ref<HTMLButtonElement>;
};
const DefaultToggle = React.memo(function DefaultToggle({
    text,
    onClick,
    'aria-expanded': ariaExpanded,
    ref,
    ...props
}: DefaultToggleProps) {
    return (
        <Button ref={ref} onClick={onClick} text={text} aria-expanded={ariaExpanded} {...props} />
    );
});

type NestedToggleProps = {
    text: string;
    onClick: (e: React.MouseEvent) => void;
    triggerOnHover?: boolean;
    'aria-expanded'?: Booleanish;
    icon?: string;
    iconClassName?: string;
    className?: string;
    ref?: React.Ref<HTMLAnchorElement>;
};
const NestedToggle = React.memo(function NestedToggle({
    text,
    onClick,
    triggerOnHover = true,
    'aria-expanded': ariaExpanded,
    icon = '',
    iconClassName = 'material-symbols-outlined',
    className = '',
    ref,
    ...props
}: NestedToggleProps) {
    const onMouseEnter = React.useCallback(
        (e: React.MouseEvent) => {
            triggerOnHover && onClick(e);
        },
        [onClick, triggerOnHover],
    );

    let iconNode = null;
    if (icon) {
        iconNode = <i className={`icon-left ${iconClassName}`}>{icon}</i>;
    }
    return (
        <a
            className={`dropdown-item nested-toggle d-flex ${className}`}
            onMouseEnter={onMouseEnter}
            ref={ref}
            onClick={onClick}
            aria-expanded={ariaExpanded}
            {...props}
        >
            {iconNode}
            {text}
            <i className="material-symbols-outlined ms-l5-auto icon-right">chevron_right</i>
        </a>
    );
});

const MULTITEXT_ITEM_WIDTH = 18; // similar to grid system: if there are 2 items, each item takes up 24 / 2 = 12rem

const DropdownItem = React.memo(function DropdownItem({
    text,
    onClick,
    onSelectData,
    onSelect,
    icon = '',
    iconClassName,
    className,
    textStyle,
    children,
    disabled = false,
    href,
    active,
    as: Component,
    textArray = Immutable.List<ImmutableMap<DropdownItemMultiText>>(),
    ref,
    ...props
}: {
    id: string;
    text?: string;
    onClick: (event: React.MouseEvent) => void;
    onSelectData: any;
    onSelect: (data: any, event: React.MouseEvent) => void;
    icon?: string;
    iconClassName?: string;
    className?: string;
    textStyle?: DROPDOWN_ITEM_TEXT_STYLES;
    children?: React.ReactNode;
    disabled?: boolean;
    href?: string;
    active?: boolean;
    as: React.ComponentType<any>;
    textArray?: Immutable.List<ImmutableMap<DropdownItemMultiText>>;
    ref?: React.Ref<HTMLAnchorElement>;
}) {
    const onSelectCtx = React.useContext(SelectableContext);

    const handleClick = React.useCallback(
        (event: React.MouseEvent) => {
            if (disabled) {
                return;
            }
            if (onClick) {
                onClick(event);
            }
            if (onSelectCtx) {
                // sends the onSelect call to the parent dropdown
                onSelectCtx(onSelectData, event);
            }
            if (onSelect) {
                onSelect(onSelectData, event);
            }
        },
        [disabled, onClick, onSelect, onSelectCtx, onSelectData],
    );

    let iconNode = null;
    if (icon) {
        if (icon === SPINNER_ICON) {
            iconNode = (
                <div className="icon-left">
                    <Spinner size="sm" className={iconClassName} />
                </div>
            );
        } else {
            iconNode = (
                <i className={classNames('icon-left material-symbols-outlined', iconClassName)}>
                    {CUSTOM_ICONS_MAP[icon] || icon}
                </i>
            );
        }
    }

    const expectedProps = {
        href,
        ref,
        onClick: handleClick,
        className: classNames('dropdown-item', className, textStyle, {
            'icon-dropdown-item': !!icon,
            disabled: disabled,
            active: active,
        }),
    };

    const innerContent = (
        <>
            {iconNode}
            {text}
            {children}
        </>
    );

    if (Component) {
        return (
            <Component {...expectedProps} {...props}>
                {innerContent}
            </Component>
        );
    } else if (textArray && textArray.size && textArray.size > 0) {
        const width = Math.floor(MULTITEXT_ITEM_WIDTH / textArray.size);
        const multiTextNodes = textArray.map((textObj, idx) => {
            let content = null;
            const textObjComponent = textObj.get('component');
            if (textObjComponent) {
                const Component = textObjComponent.toJS();
                const props = convertImmutablePropsToSpreadable(
                    textObj.get('props', Immutable.Map()),
                );
                content = <Component {...props} key={`component-${idx}`} />;
            } else {
                content = textObj.get('text');
            }
            return (
                <span
                    style={{
                        width: `${width}rem`,
                    }}
                    key={`${textObj.get('text')}-${idx}`}
                    className={classNames(textObj.get('className'), textObj.get('textStyle'))}
                >
                    {content}
                </span>
            );
        });
        return (
            <a
                {...expectedProps}
                {...props}
                className={classNames(
                    'dropdown-item-multitext align-items-center',
                    expectedProps.className,
                )}
            >
                {multiTextNodes}
            </a>
        );
    } else {
        return (
            <a {...expectedProps} {...props}>
                {innerContent}
            </a>
        );
    }
});

export type DropdownItemMultiText = {
    text?: string;
    textStyle?: DROPDOWN_ITEM_TEXT_STYLES;
    className?: string;
    component?: ImmutableMap<React.ComponentType>;
    props?: ImmutableMap<any>;
};

export const DROPDOWN_DIVIDER = 'DROPDOWN_DIVIDER';
export const DROPDOWN_HEADER = 'DROPDOWN_HEADER';

/*
 * A helper function to convert an Immutable.Map into an Object that can be spread.
 *
 * For example, given:
 *
 *   props = Immutable.fromJS({className: 'a', b: {c: 1}});
 *
 * We can:
 *
 *   <ChildComponent {...convertImmutablePropsToSpreadable(props)}/>
 */
const convertImmutablePropsToSpreadable = (
    immutableProps: any,
    ignoreKeys: string[] = [],
    toJSKeys: string[] = [],
): any => {
    if (!immutableProps) {
        return undefined;
    }

    const spreadable: any = {};
    immutableProps.keySeq().forEach((k: any) => {
        if (ignoreKeys.includes(k)) {
            return;
        }
        let val = immutableProps.get(k);
        if (isImmutable(val) && toJSKeys.includes(k)) {
            val = val.toJS();
        }
        spreadable[k] = val;
    });
    return spreadable;
};

export type DropdownItemProp<SelectDataType extends object> = ImmutableMap<{
    id: string;
    text?: string;
    onSelectData?: SelectDataType;
    tooltipTitle?: string;
    tooltipContainer?: string;
    tooltipPlacement?: Placement;
    icon?: string;
    disabled?: boolean;
    itemComponent?: React.ComponentType;
    nestedDropdown?: DropdownProps<SelectDataType>;
    className?: string;
    iconClassName?: string;
    'data-testid'?: string;
    children?: React.ReactNode;
    href?: string;
}>;
export type DropdownProps<SelectDataType extends object> = {
    as?: React.ElementType; // The top-level type of element/component that should be rendered
    drop?: 'up' | 'down' | 'start' | 'end'; // the direction that the dropdown should drop
    closeOnSelect?: boolean; // whether the dropdown should close when one of the items in the dropdown is clicked
    ToggleComponent?: React.ComponentType<any>; // the Component to use to render the Toggle. Must accept a ref prop.
    toggleProps?: Collection<unknown, unknown>; // the props to pass into the ToggleComponent
    MenuComponent?: React.ComponentType; // the component to use to render the Menu
    menuProps?: Collection<unknown, unknown>; // the props to pass into the MenuComponent
    ItemComponent?: React.ComponentType<any>; // the component to use to render each Item
    itemPropsList?: Immutable.List<DropdownItemProp<SelectDataType>>; // the list of items (and the props to pass into each item)
    breakOverflow?: boolean; // whether to use fixed positioning strategy or not: https://popper.js.org/docs/v2/constructors/#strategy
    limitMenuHeight?: boolean; // whether to force the menu to scroll if it takes more space than a given maximum height
    onSelect?: (onSelectData: ImmutableMap<SelectDataType>, e: React.MouseEvent) => void /*
     * The callback when one of the items is selected.
     * Pass `onSelectData` into each of the entries in `itemPropsList`
     * to receive the data back as an argument to `onSelect`.
     */;
    afterOpen?: (path: string) => void;
    afterClose?: () => void;
    className?: string; // the top-level className to apply to the dropdown
    'data-testid'?: string;
    nested?: boolean;
    setParentActiveMenuPath?: (path: string, delay: number) => void;
    parentActiveMenuPath?: string;
    myMenuPath?: string;
    ref?: React.Ref<any>;
};
export type DropdownRef = {
    open: (path: string) => void;
    close: () => void;
};

/**
 * The Dropdown component is very flexible. You can do all sorts of things, like:
 *
 *   - render using a custom toggle component
 *   - render using a custom menu component
 *   - render using a custom item component
 *
 * But there are also defaults that provide a "normal" Lumen5 dropdown.
 *
 * Probably the best way to use the Dropdown is to memoize or store-in-state the itemPropsList
 * and include an `onSelectData` field along with each entry in the itemPropsList.
 * You can use this `onSelectData` to perform operations when the `onSelect` callback is
 * called. For example:
 *
 * // create an enum of action types
 * const actionTypes = {ONE: 'one', TWO: 'two'};
 *
 * // memoize, make these consts, or store the itemPropsList in state to avoid excessive re-renders
 * const itemPropsList = ImmutableFromJS([
 *     {
 *         id: 'one',
 *         text: 'item one!',
 *         onSelectData: {type: actionTypes.ONE, otherMetadata: 1},
 *     },
 *     {
 *         id: 'two',
 *         text: 'item two!',
 *         onSelectData: {type: actionTypes.TWO, otherMetadata: 2},
 *     },
 * ]) as Immutable.List<DropdownItemProp<YOUR_SELECT_DATA_TYPE>>;
 *
 * // then render the Dropdown wherever you need
 * <Dropdown
 *   itemPropsList={itemPropsList}
 *   onSelect={onSelect}
 *   {...props}
 *   />
 *
 * // and finally, we can make a reducer-esque onSelect callback:
 * const onSelect = React.useCallback((onSelectData: ImmutableMap<YOUR_SELECT_DATA_TYPE>) {
 *     switch(onSelectData.get('type')) {
 *         case actionTypes.ONE: {
 *             // handle action one!
 *         }
 *         case actionTypes.TWO: {
 *             // handle action two!
 *         }
 *     }
 * });
 *
 * It's probably best to look at Dropdown.stories.tsx for more examples of ways to customize!
 */
function DropdownFunc<SelectDataType extends object>({
    as = undefined,
    drop,
    closeOnSelect = true,
    ToggleComponent,
    toggleProps,
    MenuComponent,
    menuProps,
    ItemComponent,
    itemPropsList,
    breakOverflow = false,
    limitMenuHeight = false,
    onSelect,
    afterOpen,
    afterClose,
    className,
    'data-testid': dataTestId = 'dropdown',

    // internal props, used for nested dropdowns.
    // external users of the <Dropdown/> should ignore these props:
    nested = false,
    setParentActiveMenuPath,
    parentActiveMenuPath = '',
    myMenuPath = 'menu',
    ref,
}: DropdownProps<SelectDataType> & { ref?: React.Ref<DropdownRef> }) {
    /*
     * Below is all the code to handle the open/close state of the dropdown,
     * including nesting state.
     *
     * Whether or not the dropdown is showing is driven by a "menuPath". Each
     * Item inside of a dropdown has a menuPath. For nested items, the menuPath
     * is the concatenation: `${parentMenuPath}-${myMenuPath}`.
     *
     * The top-level Dropdown then maintains the currently open menuPath (the
     * activeMenuPath). Nested dropdowns do not maintain this state, they just
     * use the value from the top-level parent.
     */
    const [_activeMenuPath, _setActiveMenuPath] = React.useState('');
    const debouncedSetActiveMenuPath = (newMenuPath: string, debounceDelay: number) =>
        debounceManager.addCallback(_setActiveMenuPath, debounceDelay)(newMenuPath);

    // a setter and getter for the active menu path
    const getActiveMenuPath = React.useCallback(() => {
        if (nested) {
            return parentActiveMenuPath;
        } else {
            return _activeMenuPath;
        }
    }, [_activeMenuPath, nested, parentActiveMenuPath]);

    const setActiveMenuPath = React.useCallback(
        (newMenuPath: string, debounceTime: number = 0) => {
            if (nested) {
                typeof setParentActiveMenuPath === 'function' &&
                    setParentActiveMenuPath(newMenuPath, MOUSEENTER_DELAY_MS);
            } else {
                debouncedSetActiveMenuPath(newMenuPath, debounceTime);
            }
        },
        [nested, setParentActiveMenuPath],
    );

    // the toggling logic
    const show = getActiveMenuPath().indexOf(myMenuPath) >= 0;
    const onToggle = React.useCallback(
        (requestOpen: any, metadata: any) => {
            if (metadata.source !== 'rootClose') {
                setActiveMenuPath(myMenuPath);
            }

            if (closeOnSelect && !requestOpen && metadata.source === 'select') {
                setActiveMenuPath('');
            }

            if (!requestOpen && metadata.source === 'rootClose') {
                setActiveMenuPath('');
            }
        },
        [closeOnSelect, setActiveMenuPath, myMenuPath],
    );

    React.useEffect(() => {
        if (show) {
            typeof afterOpen === 'function' && afterOpen(myMenuPath);
        } else {
            typeof afterClose === 'function' && afterClose();
        }
    }, [afterClose, afterOpen, myMenuPath, show]);

    /*
     * this provides a way for parents that instantiate a Dropdown to
     * instruct the dropdown to close. Can be used like:
     *
     *  dropdownRef.current.close();
     */
    React.useImperativeHandle(ref, () => {
        return {
            open: (path: any) => {
                setActiveMenuPath(path);
            },
            close: () => {
                setActiveMenuPath('');
            },
        };
    });

    /*
     * Here is all the code to handle what gets rendered!
     */
    let toggleAs = ToggleComponent;
    if (!toggleAs) {
        if (nested) {
            toggleAs = NestedToggle;
        } else {
            toggleAs = DefaultToggle;
        }
    }

    if (nested && drop === undefined) {
        drop = 'end';
    }

    const spreadableMenuProps = convertImmutablePropsToSpreadable(menuProps) || {};
    if (limitMenuHeight) {
        spreadableMenuProps.className = classNames(spreadableMenuProps.className, {
            'limit-menu-height': limitMenuHeight,
        });
    }

    let propOverrides = {};
    if (breakOverflow) {
        propOverrides = {
            popperConfig: {
                strategy: 'fixed',
            },
            // if using strategy: 'fixed', there's a react-bootstrap bug that also means we need renderOnMount
            // https://github.com/react-bootstrap/react-bootstrap/issues/6203
            // If that issue is solved, we can probably remove renderOnMount from propOverrides
            renderOnMount: true,
        };
    }

    const handleDropdownItemMouseEnter = React.useCallback(
        (e: React.MouseEvent) => {
            setActiveMenuPath(myMenuPath, MOUSEENTER_DELAY_MS);
        },
        [myMenuPath, setActiveMenuPath],
    );

    return (
        <ReactBootstrapDropdown
            as={as}
            drop={drop}
            className={classNames(className, styles['lumen5-dropdown'])}
            data-testid={dataTestId}
            show={show}
            onToggle={onToggle}
            onSelect={onSelect as any} // the compiler does not know that we are supplying our own component and the SelectCallback type is not valid
        >
            <ReactBootstrapDropdown.Toggle
                as={toggleAs}
                {...convertImmutablePropsToSpreadable(toggleProps, [], ['iconLeft', 'iconRight'])}
            />
            <ReactBootstrapDropdown.Menu
                as={MenuComponent || undefined}
                {...spreadableMenuProps}
                {...propOverrides}
            >
                <div
                    className={classNames('scroller-fix', {
                        'px-l5-4': nested,
                    })}
                >
                    {itemPropsList &&
                        (itemPropsList.map((itemProps, index) => {
                            if (itemProps.get('nestedDropdown')) {
                                return (
                                    <Dropdown
                                        key={itemProps.get('id')}
                                        nested={true}
                                        setParentActiveMenuPath={setActiveMenuPath}
                                        parentActiveMenuPath={getActiveMenuPath()}
                                        myMenuPath={`${myMenuPath}-${itemProps.get('id')}`}
                                        {...convertImmutablePropsToSpreadable(
                                            itemProps.get('nestedDropdown'),
                                        )}
                                    />
                                );
                            }
                            if (itemProps.get('id') === DROPDOWN_DIVIDER) {
                                return (
                                    <ReactBootstrapDropdown.Divider
                                        key={DROPDOWN_DIVIDER + index.toString()}
                                    />
                                );
                            }
                            if (itemProps.get('id') === DROPDOWN_HEADER) {
                                if (itemProps.get('text') && itemProps.get('text') !== '') {
                                    return (
                                        <ReactBootstrapDropdown.Header
                                            key={DROPDOWN_HEADER + index.toString()}
                                            className={classNames(
                                                'dropdown-header-container',
                                                itemProps.get('className'),
                                            )}
                                        >
                                            <>
                                                {itemProps.get('icon') && (
                                                    <i className="icon-left material-symbols-outlined">
                                                        {itemProps.get('icon') as React.ReactNode}
                                                    </i>
                                                )}
                                                {itemProps.get('text')}
                                            </>
                                        </ReactBootstrapDropdown.Header>
                                    );
                                }
                            } else {
                                // if wrapped with <Tooltip/>
                                const tooltipTitle = itemProps.get('tooltipTitle');
                                if (tooltipTitle) {
                                    return (
                                        <Tooltip
                                            key={`tooltip-'${itemProps.get('id')}`}
                                            title={tooltipTitle}
                                            container={itemProps.get('tooltipContainer')}
                                            placement={itemProps.get('tooltipPlacement')}
                                        >
                                            {/* bootstrap will add attributes to the element that the tooltip text needs to be
                                            appended to. However, for components that have a disabled state, these attributes
                                            don't seem to show if the item is in the disabled state. A workaround is to wrap
                                            a container so the tooltip attributes will be modified on the <div/>, and the
                                            tooltip will show even if the component item is disabled. */}
                                            {/* same issue appears in <Button/> */}
                                            <div>
                                                <DropdownItem
                                                    key={itemProps.get('id')}
                                                    as={
                                                        itemProps.get('itemComponent')?.toJS() ||
                                                        ItemComponent
                                                    }
                                                    onMouseEnter={handleDropdownItemMouseEnter}
                                                    {...convertImmutablePropsToSpreadable(
                                                        itemProps,
                                                        [
                                                            'itemComponent',
                                                            'tooltipTitle',
                                                            'tooltipContainer',
                                                            'tooltipPlacement',
                                                        ],
                                                        ['children'],
                                                    )}
                                                />
                                            </div>
                                        </Tooltip>
                                    );
                                }

                                return (
                                    <DropdownItem
                                        key={itemProps.get('id')}
                                        as={itemProps.get('itemComponent')?.toJS() || ItemComponent}
                                        onMouseEnter={handleDropdownItemMouseEnter}
                                        {...convertImmutablePropsToSpreadable(
                                            itemProps,
                                            [
                                                'itemComponent',
                                                'tooltipTitle',
                                                'tooltipContainer',
                                                'tooltipPlacement',
                                            ],
                                            ['children'],
                                        )}
                                    />
                                );
                            }
                        }) as React.ReactNode)}
                </div>
            </ReactBootstrapDropdown.Menu>
        </ReactBootstrapDropdown>
    );
}

export const Dropdown = <T extends object>({
    myRef,
    ...rest
}: DropdownProps<T> & { myRef?: React.Ref<DropdownRef> }) => <DropdownFunc {...rest} ref={myRef} />;
